import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useMatch } from 'react-router-dom';
import { Grid, styled, useTheme } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchAreaDetails } from '../../state/features/areaDetailsSlice';
import { fetchAreas } from '../../state/features/mapSlice';
import { privateRoutes } from '../../routes/appRouteList';
import { AreaMetrics } from '../../interfaces/services/areas';
import { ApiStatus } from '../../utils/enums';
import { useFilters } from '../Filters/useFilters';
import Revenue from '../Widgets/Revenue';
import AverageRate from '../Widgets/AverageRate';
import Transit from '../Widgets/Transit';
import AreaMap from '../Map/AreaMap';
import Performance from '../Widgets/Performance';
import Volume from '../Widgets/Volume';
import AreaBalance from '../Widgets/AreaBalance';

const classesPrefix = 'areaDetails';

const classes = {
    statContainer: `${classesPrefix}-statContainer`,
    revenueContainer: `${classesPrefix}-revenueContainer`,
    rateContainer: `${classesPrefix}-rateContainer`,
    volumeContainer: `${classesPrefix}-volumeContainer`,
    mapContainer: `${classesPrefix}-mapContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.statContainer}`]: {
            padding: theme.spacing(2),
            marginBottom: 0,
            display: 'flex',
            alignItems: 'stretch',
            order: 1,
        },
        [`& .${classes.revenueContainer}`]: {
            order: 1,
        },
        [`& .${classes.rateContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.volumeContainer}`]: {
            [theme.breakpoints.down('lg')]: {
                order: 3,
            },
        },
        [`& .${classes.mapContainer}`]: {
            display: 'flex',
            flexDirection: 'column',
            order: 2,
            [theme.breakpoints.down('lg')]: {
                order: 1,
            },
        },
    };
});

const AreaDetails = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const match = useMatch(privateRoutes.areas);
    const filters = useFilters();

    const areaName = match?.params.name;

    const areaDetailsApiStatus = useAppSelector((state) => { return state.area.detailsApiStatus; });
    const areaDetails = useAppSelector((state) => { return state.area.details; });

    const inboundOrdersApiStatus = useAppSelector((state) => { return state.area.inboundOrdersApiStatus; });
    const inboundOrders = useAppSelector((state) => { return state.area.inboundOrders; });

    const outboundOrdersApiStatus = useAppSelector((state) => { return state.area.outboundOrdersApiStatus; });
    const outboundOrders = useAppSelector((state) => { return state.area.outboundOrders; });

    const areasApiStatus = useAppSelector((state) => { return state.map.areasApiStatus; });
    const areas = useAppSelector((state) => { return state.map.areas; });

    useEffect(() => {
        if (inboundOrdersApiStatus === ApiStatus.Success &&
            outboundOrdersApiStatus === ApiStatus.Success &&
            inboundOrders.length === 0 &&
            outboundOrders.length === 0
        ) {
            toast.info("We didn't find any orders into or out of this area that match your search criteria. Try adjusting your filters");
        }
    }, [inboundOrdersApiStatus, inboundOrders.length, outboundOrdersApiStatus, outboundOrders.length]);

    useEffect(() => {
        if (areaName) {
            dispatch(fetchAreaDetails({
                service_area_id: areaName,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, areaName, filters]);

    useEffect(() => {
        if (areaName) {
            dispatch(fetchAreas({
                origin: areaName,
                destination: areaName,
            }));
        }
    }, [dispatch, areaName]);

    const sumDetailValue = (value: keyof AreaMetrics): number | null => {
        if (!areaDetails) {
            return null;
        }

        if ((!areaDetails.inbound_metrics || areaDetails.inbound_metrics[value] === null) &&
            (!areaDetails.outbound_metrics || areaDetails.outbound_metrics[value] === null) &&
            (!areaDetails.service_area_metrics || areaDetails.service_area_metrics[value] === null)) {
            return null;
        }

        const inboundMetric = areaDetails.inbound_metrics && areaDetails.inbound_metrics[value] !== null ? areaDetails.inbound_metrics[value] : 0;
        const outboundMetric = areaDetails.outbound_metrics && areaDetails.outbound_metrics[value] !== null ? areaDetails.outbound_metrics[value] : 0;
        const serviceAreaMetric = areaDetails.service_area_metrics && areaDetails.service_area_metrics[value] !== null ? areaDetails.service_area_metrics[value] : 0;

        return (inboundMetric || 0) +
            (outboundMetric || 0) +
            (serviceAreaMetric || 0);
    };

    const loading = areaDetailsApiStatus === ApiStatus.Idle || areaDetailsApiStatus === ApiStatus.Loading;

    const totalRevenue = sumDetailValue('total_revenue_usd_amount');
    const linehaulRevenue = sumDetailValue('linehaul_revenue_usd_amount');
    const fuelRevenue = sumDetailValue('fuel_surcharge_usd_amount');
    const accessorialRevenue = sumDetailValue('accessorial_revenue_usd_amount');
    const loadedMiles = sumDetailValue('loaded_distance_miles_amount');
    const totalMiles = sumDetailValue('travel_distance_miles_amount');
    const emptyMiles = sumDetailValue('empty_distance_miles_amount');
    const transitHours = sumDetailValue('first_stop_departure_to_final_stop_arrival_hours_duration');
    const dwellTime = sumDetailValue('netwise_dwell_hours_duration');
    const inTransitDelayHours = sumDetailValue('netwise_in_transit_delay_hours_duration');
    const orderVolume = sumDetailValue('freight_order_count');
    const loadTime = sumDetailValue('load_hours_duration');
    const unloadTime = sumDetailValue('unload_hours_duration');
    const emptyHours = sumDetailValue('empty_transit_hours_duration');

    return (
        <StyledGrid container>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.revenueContainer}`}
                >
                    <Revenue
                        loading={loading}
                        totalRevenue={totalRevenue}
                        linehaulRevenue={linehaulRevenue}
                        fuelRevenue={fuelRevenue}
                        accessorialRevenue={accessorialRevenue}
                        orderVolume={orderVolume}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.rateContainer}`}
                >
                    <AverageRate
                        loading={loading}
                        totalRevenue={totalRevenue}
                        linehaulRevenue={linehaulRevenue}
                        fuelRevenue={fuelRevenue}
                        accessorialRevenue={accessorialRevenue}
                        loadedMiles={loadedMiles}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg
                    className={`${classes.statContainer} ${classes.volumeContainer}`}
                >
                    <Grid
                        container
                        direction='column'
                        sx={{
                            alignItems: 'stretch',
                        }}
                    >
                        <Grid item xs>
                            <Volume
                                loading={loading}
                                orderVolume={orderVolume}
                            />
                        </Grid>
                        <Grid item xs='auto'>
                            <AreaBalance
                                title='Daily Area Balance'
                                apiStatus={areaDetailsApiStatus}
                                areaYield={areaDetails?.service_area_yield_usd}
                                inboundOrders={areaDetails?.inbound_metrics?.freight_order_count || 0}
                                outboundOrders={areaDetails?.outbound_metrics?.freight_order_count || 0}
                                intraAreaOrders={areaDetails?.service_area_metrics?.freight_order_count || 0}
                                isDailyBalance={true}
                                averageBalance={areaDetails?.average_daily_balance}
                                enableNavigationClick={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    lg={4}
                    className={classes.mapContainer}
                >
                    <AreaMap
                        isFetchingData={areasApiStatus === ApiStatus.Idle || areasApiStatus === ApiStatus.Loading || loading}
                        areaName={areaDetails?.service_area_name || areaName || ''}
                        areas={areas}
                        theme={theme}
                    />
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md lg={8} className={classes.statContainer}>
                    <Transit
                        loading={loading}
                        orderVolume={orderVolume}
                        transitHours={transitHours}
                        dwellTime={dwellTime}
                        inTransitDelayHours={inTransitDelayHours}
                        emptyMiles={emptyMiles}
                        loadedMiles={loadedMiles}
                        loadTime={loadTime}
                        unloadTime={unloadTime}
                        totalRevenue={totalRevenue}
                        totalMiles={totalMiles}
                        emptyHours={emptyHours}
                    />
                </Grid>
                <Grid item xs={12} md lg={4} className={classes.statContainer}>
                    <Performance
                        orders={inboundOrders.concat(outboundOrders)}
                        loadingTopShippers={inboundOrdersApiStatus === ApiStatus.Loading || outboundOrdersApiStatus === ApiStatus.Loading}
                        inboundId={areaName ?? null}
                        outboundId={areaName ?? null}
                    />
                </Grid>
            </Grid>
        </StyledGrid>
    );
};

export default AreaDetails;
